import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const TestimonialsEditor = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    quote: '',
    author: '',
    role: '',
    avatar_url: '',
    order: 0
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingTestimonial) {
        await axios.put(`${BACKEND_URL}/api/admin/testimonials/${editingTestimonial.id}`, formData);
        toast({ title: 'Success', description: 'Testimonial updated successfully' });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/testimonials`, formData);
        toast({ title: 'Success', description: 'Testimonial created successfully' });
      }
      setIsOpen(false);
      resetForm();
      fetchTestimonials();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save testimonial', variant: 'destructive' });
    }
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData(testimonial);
    setIsOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/testimonials/${id}`);
      toast({ title: 'Success', description: 'Testimonial deleted successfully' });
      fetchTestimonials();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete testimonial', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ quote: '', author: '', role: '', avatar_url: '', order: 0 });
    setEditingTestimonial(null);
  };

  return (
    <Card className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Testimonials</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-pink-500 hover:bg-pink-600">
              <Plus className="w-4 h-4 mr-2" />
              Add Testimonial
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Quote</Label>
                <Textarea
                  value={formData.quote}
                  onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                  placeholder="Customer testimonial quote"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Author Name</Label>
                  <Input
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    placeholder="e.g., Alex Taylor"
                  />
                </div>
                <div>
                  <Label>Role/Title</Label>
                  <Input
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    placeholder="e.g., E-commerce Manager"
                  />
                </div>
              </div>
              <div>
                <Label>Avatar URL</Label>
                <Input
                  value={formData.avatar_url}
                  onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
                  placeholder="Paste from Media Library"
                />
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                />
              </div>
              <Button onClick={handleSubmit} className="w-full">
                {editingTestimonial ? 'Update' : 'Create'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {testimonials.map((testimonial) => (
          <Card key={testimonial.id} className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src={testimonial.avatar_url} 
                alt={testimonial.author} 
                className="w-16 h-16 rounded-full object-cover"
              />
              <div>
                <h3 className="font-bold">{testimonial.author}</h3>
                <p className="text-sm text-gray-600">{testimonial.role}</p>
              </div>
            </div>
            <p className="text-sm italic text-gray-700 mb-4">"{testimonial.quote}"</p>
            <p className="text-xs text-gray-500 mb-4">Order: {testimonial.order}</p>
            <div className="flex space-x-2">
              <Button size="sm" variant="outline" onClick={() => handleEdit(testimonial)} className="flex-1">
                <Edit className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="destructive" onClick={() => handleDelete(testimonial.id)} className="flex-1">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {testimonials.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No testimonials added yet
        </div>
      )}
    </Card>
  );
};

export default TestimonialsEditor;
